export const checkIfUserHasTag = ({ users, column, result, tagId }) => {
  if (result.destination.droppableId === result.source.droppableId) {
    return false
  }
  const targetTagCategory = column[result.destination.droppableId]
  // Uncategorized can hold any number of a user's tags, so it never conflicts.
  // Without this guard its `column-1` id would parse as category id 1 below.
  if (targetTagCategory.id === 'column-1') {
    return false
  }
  // Column ids are `category-${category_id}`, so split to grab the raw id
  const targetTagCategoryId = parseInt(targetTagCategory.id.split('-')[1], 10)
  const draggedTagId = parseInt(tagId, 10)

  const usersAssociatedWithThisTag = users.filter((user) =>
    user.tags.some((userTag) => userTag.id === draggedTagId)
  )

  // A user should only have one tag per category, so moving the tag into a
  // category where any of its users already hold a tag must be blocked
  return usersAssociatedWithThisTag.some((user) =>
    user.tags.some((userTag) => userTag.tag_category_id === targetTagCategoryId)
  )
}

export const buildUpdatedUsers = ({ users, column, result, draggableId }) => {
  const draggedTagId = parseInt(draggableId.split('-')[1], 10)
  const destinationColumn = column[result.destination.droppableId]
  const tagCategoryId =
    destinationColumn.id === 'column-1' ? null : parseInt(destinationColumn.id.split('-')[1], 10)

  return users.map((user) => {
    const newTags = user.tags.map((userTag) => {
      if (userTag.id !== draggedTagId) {
        return userTag
      }

      return { ...userTag, tag_category_id: tagCategoryId }
    })

    return { ...user, tags: newTags }
  })
}

export const moveTagInState = ({ tagsState, result }) => {
  const { destination, source, draggableId } = result
  const start = tagsState.column[source.droppableId]
  const finish = tagsState.column[destination.droppableId]

  if (start === finish) {
    const newTaskIds = Array.from(start.taskIds)
    newTaskIds.splice(source.index, 1)
    newTaskIds.splice(destination.index, 0, draggableId)
    return {
      ...tagsState,
      column: {
        ...tagsState.column,
        [start.id]: { ...start, taskIds: newTaskIds },
      },
    }
  }

  const startTaskIds = Array.from(start.taskIds)
  startTaskIds.splice(source.index, 1)
  const finishTaskIds = Array.from(finish.taskIds)
  finishTaskIds.splice(destination.index, 0, draggableId)

  return {
    ...tagsState,
    column: {
      ...tagsState.column,
      [start.id]: { ...start, taskIds: startTaskIds },
      [finish.id]: { ...finish, taskIds: finishTaskIds },
    },
  }
}

export const reorderColumns = ({ tagsState, result }) => {
  const newColumnOrder = Array.from(tagsState.columnOrder)
  newColumnOrder.splice(result.source.index, 1)
  newColumnOrder.splice(result.destination.index, 0, result.draggableId)

  return { ...tagsState, columnOrder: newColumnOrder }
}

export const computeSortOrderUpdates = ({ categories, oldIndex, newIndex, draggableId }) => {
  const movedCategory = categories.find((category) => category.id === draggableId)
  const isMovingDown = newIndex > oldIndex
  const affected = categories
    .filter((category) =>
      isMovingDown
        ? category.sortId > oldIndex && category.sortId <= newIndex
        : category.sortId < oldIndex && category.sortId >= newIndex
    )
    .map((category) => ({
      category,
      updates: { sort_id: isMovingDown ? category.sortId - 1 : category.sortId + 1 },
    }))

  return { affected, moved: { category: movedCategory, updates: { sort_id: newIndex } } }
}

export const createDragEndHandler =
  ({ tagsState, users, onColumnReorder, onTagMove, onConflict }) =>
  (result) => {
    const { destination, source, type, draggableId } = result
    if (!destination) {
      return
    }
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return
    }

    if (type === 'column') {
      onColumnReorder(result)
      return
    }

    // draggableId is `tag-${tag_id}`, the conflict check needs the raw tag id
    const tagId = draggableId.split('-')[1]
    if (checkIfUserHasTag({ users, column: tagsState.column, result, tagId })) {
      onConflict(result)
    } else {
      onTagMove(result)
    }
  }

export const hasDuplicateTags = ({ existingTags, newNames }) => {
  const allOrgTagNames = Object.values(existingTags).map((tag) => tag.content.toLowerCase())
  newNames.forEach((tagName) =>
    allOrgTagNames.push(tagName.toLowerCase().trim().split(' ').join(''))
  )

  return new Set(allOrgTagNames).size !== allOrgTagNames.length
}

export const isDuplicateCategory = ({ columns, name }) => {
  const normalize = (value) => value.toLowerCase().trim().split(' ').join('')

  return Object.values(columns).some((column) => normalize(column.title) === normalize(name))
}
